import { signal } from "@preact/signals";

// PWA-Installation aktiv anbieten (statt auf die Browser-Laune zu warten): wir fangen das
// `beforeinstallprompt`-Event ab und lösen es später per Button aus (promptInstall).
// Muss früh importiert werden (main.tsx), damit der Listener vor dem Event steht.

interface InstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

/** Läuft die App bereits als installierte PWA (Standalone)? */
export const isStandalone = signal(
  (typeof matchMedia === "function" && matchMedia("(display-mode: standalone)").matches) ||
    (navigator as unknown as { standalone?: boolean }).standalone === true,
);

/** iOS/Safari kennt kein beforeinstallprompt → dort nur manuell über „Teilen → Home-Bildschirm". */
export const isIOS =
  /iphone|ipad|ipod/i.test(navigator.userAgent) ||
  // iPadOS meldet sich als Mac mit Touch
  (navigator.platform === "MacIntel" && (navigator as unknown as { maxTouchPoints?: number }).maxTouchPoints! > 1);

/** true, solange ein abgefangenes Prompt bereitliegt (→ „Installieren"-Button zeigen). */
export const canInstall = signal(false);

let deferred: InstallPromptEvent | null = null;

window.addEventListener("beforeinstallprompt", (e) => {
  e.preventDefault(); // Chrome-eigene Mini-Infobar unterdrücken; wir bieten es selbst an
  deferred = e as InstallPromptEvent;
  canInstall.value = true;
});

window.addEventListener("appinstalled", () => {
  deferred = null;
  canInstall.value = false;
  isStandalone.value = true;
});

/** Zeigt den nativen Installations-Dialog (nur nach User-Geste aufrufen). */
export async function promptInstall(): Promise<void> {
  const e = deferred;
  if (!e) return;
  deferred = null;
  canInstall.value = false;
  try {
    await e.prompt();
    await e.userChoice;
  } catch {
    /* abgebrochen – egal */
  }
}
